import { useEffect } from 'react';

/**
 * Custom hook to manage dynamic favicon switching based on system theme preference
 * Updates favicon links in the document head when system theme changes
 * This responds to the user's OS/browser theme settings, not the manual toggle
 */
export const useFavicon = () => {
  useEffect(() => {
    const updateFavicon = () => {
      // Check system preference for dark mode
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      // Define favicon paths for light and dark modes
      const lightFavicon = '/logo circle.png';
      const darkFavicon = '/dark mode logo.png';
      
      // Select the appropriate favicon based on system preference
      const faviconPath = prefersDark ? darkFavicon : lightFavicon;
      
      // Update all favicon links in the document head
      const faviconSelectors = [
        'link[rel="icon"]',
        'link[rel="shortcut icon"]',
        'link[rel="apple-touch-icon"]'
      ];
      
      faviconSelectors.forEach(selector => {
        const links = document.querySelectorAll(selector);
        links.forEach(link => {
          const linkElement = link as HTMLLinkElement;
          linkElement.href = faviconPath;
        });
      });
      
      // Also update the theme-color meta tag for better mobile experience
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', prefersDark ? '#0a0a0a' : '#ffffff');
      }
    };
    
    // Update favicon immediately on mount
    updateFavicon();
    
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      updateFavicon();
    };
    
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    // Cleanup listener on unmount
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []); // Empty dependency array - only run on mount/unmount
};
